/**
 * Code + PIN identity.
 *
 * There is no email in this app's sign-in flow, so an account is identified by
 * a generated access code. Auth still runs on the backend's email/password
 * engine, so the code is mapped to a synthetic address that never receives
 * mail, and the 6-digit PIN is the password.
 */

/** Ambiguous characters (O/0, I/1/L, U/V) are excluded so codes are readable. */
const ALPHABET = "ABCDEFGHJKMNPQRSTWXYZ23456789";
const BODY_LENGTH = 6;
const PREFIX = "SGL";
const EMAIL_DOMAIN = "signal.local";

export const PIN_LENGTH = 6;
export const LAST_CODE_STORAGE_KEY = "signal.last-access-code";

/** e.g. `SGL-7K4M2P` */
export function generateAccessCode(): string {
  const bytes = new Uint32Array(BODY_LENGTH);
  crypto.getRandomValues(bytes);
  let body = "";
  for (const n of bytes) body += ALPHABET[n % ALPHABET.length];
  return `${PREFIX}-${body}`;
}

/** Accepts sloppy input (lowercase, spaces, missing dash) and returns the canonical form. */
export function normalizeAccessCode(input: string): string {
  const raw = input.toUpperCase().replace(/[^A-Z0-9]/g, "");
  const body = raw.startsWith(PREFIX) ? raw.slice(PREFIX.length) : raw;
  return body ? `${PREFIX}-${body}` : "";
}

export function isValidAccessCode(input: string): boolean {
  return new RegExp(`^${PREFIX}-[A-Z0-9]{${BODY_LENGTH}}$`).test(normalizeAccessCode(input));
}

export function isValidPin(pin: string): boolean {
  return new RegExp(`^\\d{${PIN_LENGTH}}$`).test(pin);
}

export function digitsOnly(value: string): string {
  return value.replace(/\D/g, "").slice(0, PIN_LENGTH);
}

/** The synthetic address the auth engine stores. Never shown, never mailed. */
export function codeToEmail(code: string): string {
  return `${normalizeAccessCode(code).toLowerCase()}@${EMAIL_DOMAIN}`;
}

/** Recovers the code from a synthetic address, for display. */
export function emailToCode(email: string | null | undefined): string | null {
  if (!email || !email.endsWith(`@${EMAIL_DOMAIN}`)) return null;
  return normalizeAccessCode(email.slice(0, -(EMAIL_DOMAIN.length + 1)));
}
